/**
 * Lightweight requirement directory for the editor-side mapping surfaces:
 * titles + classes for the bid's compliance items and evaluation criteria.
 * Module-cached per bid — the brief block and picker share one fetch.
 */

import { useEffect, useState } from 'react'
import { api } from '@/lib/api'
import { formatReqRef } from '@/lib/requirement-refs'
import type { CriticalityValue, ResponseExpectation } from '@/types/extraction'

export interface RequirementEntry {
  id: string
  kind: 'compliance' | 'criterion'
  title: string
  /** Compliance only. */
  responseExpectation?: ResponseExpectation
  /** Compliance only — drives the matrix's "M" marker and the brief's Mandatory tag. */
  criticality?: CriticalityValue
  /** Compliance only — the bid's stable REQ number (mig 194); null while awaiting review. */
  displayNumber?: number | null
  /** Criteria only. */
  weightPercentage?: number | null
  parentId?: string | null
  /** Criteria only: parents are rollups — not mappable. */
  isLeaf: boolean
}

interface ComplianceItemRow {
  id: string
  title: string
  displayNumber?: number | null
  responseExpectation?: ResponseExpectation
  criticality?: CriticalityValue
}

interface CriterionRow {
  id: string
  title: string
  parentId?: string | null
  weightPercentage?: number | null
}

const directoryCache = new Map<string, Promise<RequirementEntry[]>>()

async function fetchDirectory(bidId: string): Promise<RequirementEntry[]> {
  const [complianceItems, criteria] = await Promise.all([
    api.get<ComplianceItemRow[]>(`/bids/${bidId}/compliance-items`),
    api.get<CriterionRow[]>(`/bids/${bidId}/evaluation-criteria`),
  ])

  const parentIds = new Set(criteria.map((c) => c.parentId).filter(Boolean))

  return [
    ...complianceItems.map((item): RequirementEntry => ({
      id: item.id,
      kind: 'compliance',
      title: item.title,
      responseExpectation: item.responseExpectation ?? 'narrative',
      ...(item.criticality ? { criticality: item.criticality } : {}),
      displayNumber: item.displayNumber ?? null,
      isLeaf: true,
    })),
    ...criteria.map((criterion): RequirementEntry => ({
      id: criterion.id,
      kind: 'criterion',
      title: criterion.title,
      weightPercentage: criterion.weightPercentage ?? null,
      parentId: criterion.parentId ?? null,
      isLeaf: !parentIds.has(criterion.id),
    })),
  ]
}

/**
 * Compliance item id → "REQ-n" for every numbered requirement in a directory.
 * Items without a number are absent, so callers fall back to their own label.
 */
export function requirementRefLabels(entries: RequirementEntry[] | null): Record<string, string> {
  const labels: Record<string, string> = {}
  for (const entry of entries ?? []) {
    if (entry.kind !== 'compliance') continue
    const ref = formatReqRef(entry.displayNumber)
    if (ref) labels[entry.id] = ref
  }
  return labels
}

export function invalidateRequirementDirectory(bidId: string): void {
  directoryCache.delete(bidId)
}

/** `idle` = no bid supplied, so nothing was ever requested — distinct from a
 *  fetch that is still in flight (`loading`) or that came back empty (`loaded`). */
export type RequirementDirectoryStatus = 'idle' | 'loading' | 'loaded' | 'failed'

export interface RequirementDirectoryResult {
  /** `null` while idle/loading; `[]` on failure so list consumers stay renderable. */
  entries: RequirementEntry[] | null
  status: RequirementDirectoryStatus
}

const IDLE: RequirementDirectoryResult = { entries: null, status: 'idle' }
const PENDING: RequirementDirectoryResult = { entries: null, status: 'loading' }

export function useRequirementDirectory(bidId: string | null): RequirementDirectoryResult {
  const [result, setResult] = useState<RequirementDirectoryResult>(() =>
    bidId ? PENDING : IDLE
  )

  useEffect(() => {
    // Functional resets: returning `prev` when the sentinel already matches
    // keeps React from re-rendering, so neither branch can loop.
    if (!bidId) {
      setResult((prev) => (prev === IDLE ? prev : IDLE))
      return
    }
    let alive = true
    // Reset so a bid switch never shows the previous bid's entries. A cached
    // bid re-resolves on the next microtask, so this is not a visible flash.
    setResult((prev) => (prev === PENDING ? prev : PENDING))
    let cached = directoryCache.get(bidId)
    if (!cached) {
      cached = fetchDirectory(bidId)
      directoryCache.set(bidId, cached)
      cached.catch(() => directoryCache.delete(bidId))
    }
    void cached.then((list) => {
      if (alive) setResult({ entries: list, status: 'loaded' })
    }).catch(() => {
      if (alive) setResult({ entries: [], status: 'failed' })
    })
    return () => {
      alive = false
    }
  }, [bidId])

  return result
}
